import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import SubscribeModal from "@/components/SubscribeModal";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Calendar, Crown, AlertCircle, Loader2 } from "lucide-react";

export default function Account() {
  const { user, loading, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  const loadSub = async () => {
    try {
      const r = await api.get("/payment/subscription");
      setSub(r.data);
    } catch {}
  };

  useEffect(() => { if (user) loadSub(); }, [user]);

  if (!user) return null;
  const exp = sub?.subscription_expires_at ? new Date(sub.subscription_expires_at) : null;
  const cancelled = !!sub?.cancelled_at;

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/payment/cancel");
      toast.success("Membership cancelled. You keep access until the end of your billing period.");
      await loadSub();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel.");
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    setBusy(true);
    try {
      await api.post("/payment/resume");
      toast.success("Welcome back! Your membership will continue to renew.");
      await loadSub();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white">
      <Header />
      <div className="pt-28 max-w-4xl mx-auto px-6 md:px-10 pb-20">
        <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-3">Account</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Your membership</h1>

        <Card className="mt-10 bg-[#141419] border-white/10 text-white">
          <CardContent className="p-8">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 ring-1 ring-white/15">
                <AvatarImage src={user.picture} />
                <AvatarFallback className="bg-[#E50914]">{user.name?.[0]}</AvatarFallback>
              </Avatar>
              <div>
                <div className="font-display text-xl font-bold">{user.name}</div>
                <div className="text-gray-400 text-sm">{user.email}</div>
              </div>
              <div className="ml-auto">
                {user.is_subscribed ? (
                  cancelled ? (
                    <Badge data-testid="badge-cancelled" variant="outline" className="border-[#FFD700]/40 text-[#FFD700]">
                      <AlertCircle className="w-3.5 h-3.5 mr-1" /> Cancelling
                    </Badge>
                  ) : (
                    <Badge data-testid="badge-active" className="bg-[#E50914] hover:bg-[#E50914] text-white">
                      <Crown className="w-3.5 h-3.5 mr-1" /> Premium
                    </Badge>
                  )
                ) : (
                  <Badge data-testid="badge-free" variant="outline" className="border-white/20 text-gray-300">Free</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6 bg-[#141419] border-white/10 text-white">
          <CardContent className="p-8">
            <h2 className="font-display text-xl font-bold mb-5">Plan</h2>

            {user.is_subscribed ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gray-200">
                  <Check className="w-5 h-5 text-[#E50914]" />
                  <span><strong>YBTV Premium</strong> — $3.99/month</span>
                </div>

                {exp && (
                  <div className="flex items-center gap-3 text-gray-300">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    {cancelled ? (
                      <span data-testid="ends-on">
                        Access ends on <strong>{exp.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</strong>
                      </span>
                    ) : (
                      <span data-testid="renews-on">
                        Renews on <strong>{exp.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</strong>
                      </span>
                    )}
                  </div>
                )}

                {cancelled && (
                  <div className="mt-4 rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/5 p-4 text-sm text-[#FFD700]/90 flex gap-3">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      Your membership won't renew. You'll keep full access until <strong>{exp?.toLocaleDateString()}</strong>, then your account drops to Free. Changed your mind? Resume any time.
                    </div>
                  </div>
                )}

                <div className="pt-4 flex flex-wrap gap-3">
                  {cancelled ? (
                    <Button
                      data-testid="resume-btn"
                      onClick={resume}
                      disabled={busy}
                      className="rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-6 py-5 font-semibold"
                    >
                      {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/> Working…</> : "Resume membership"}
                    </Button>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          data-testid="cancel-btn"
                          variant="outline"
                          className="rounded-full border-white/20 text-white hover:bg-white/5 px-6 py-5 font-semibold"
                        >
                          Cancel membership
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#141419] border-white/10 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-display text-xl">Cancel your membership?</AlertDialogTitle>
                          <AlertDialogDescription className="text-gray-400">
                            You'll keep full access until <strong className="text-white">{exp?.toLocaleDateString()}</strong>. After that, your account drops to Free and premium content will be locked again.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-keep" className="bg-transparent border-white/20 text-white hover:bg-white/5">
                            Keep membership
                          </AlertDialogCancel>
                          <AlertDialogAction
                            data-testid="cancel-confirm"
                            onClick={cancel}
                            className="bg-[#E50914] hover:bg-[#F40612] text-white"
                          >
                            Yes, cancel
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <p className="text-gray-300">
                  You're on the free preview. Unlock the entire library — movies, shows, trailers and interviews — for $3.99/month.
                </p>
                <Button
                  data-testid="account-subscribe-btn"
                  onClick={() => setOpen(true)}
                  className="rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-7 py-6 text-base font-semibold"
                >
                  Subscribe now
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-8">
          <Button
            data-testid="account-logout"
            variant="ghost"
            onClick={async () => { await logout(); navigate("/"); }}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            Sign out
          </Button>
        </div>

        <AccountSecurityRemoved />
      </div>

      <SubscribeModal open={open} onOpenChange={setOpen} onSuccess={() => { setOpen(false); refresh(); loadSub(); }} />
    </div>
  );
}

// Account security (password/email) removed — auth is Google OAuth only.
function AccountSecurityRemoved() { return null; }
